import { Button, Flex, Input, Table, Tooltip } from 'antd';
import { Fragment, useCallback, useState } from 'react';
import { InputStatus } from 'antd/es/_util/statusUtils';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { IPlan } from '@leedpulse-monorepo/interfaces';
import { PlanEditionFormContextProps } from '../../contexts/forms/plans/PlanEditionFormContext';
import { IPlanLink } from '@leedpulse-monorepo/interfaces';

interface PlanLinksListProps {
  wipPlan: IPlan;
  planEditionFormContext: PlanEditionFormContextProps;
}

const PlanLinksList: React.FC<PlanLinksListProps> = ({
  wipPlan,
  planEditionFormContext,
}) => {
  const [wipNewPlanLink, setWipNewPlanLink] = useState<IPlanLink>({
    title: '',
    url: '',
  });
  const [wipNewPlanLinkStatus, setWipNewPlanLinkStatus] =
    useState<InputStatus>('');

  const [editingPlanLinkIndex, setEditingPlanLinkIndex] = useState<
    number | null
  >(null);
  const [editingPlanLinkValue, setEditingPlanLinkValue] =
    useState<IPlanLink | null>(null);
  const [editingPlanLinkStatus, setEditingPlanLinkStatus] =
    useState<InputStatus>('');

  const validatePlanLink = useCallback(
    (
      value: IPlanLink,
      setStatus: React.Dispatch<React.SetStateAction<InputStatus>>,
      index = -1
    ) => {
      // No empty string
      if (value.title.trim() === '' || value.url.trim() === '') {
        setStatus('error');
        return false;
      }

      // No duplicate
      if (
        wipPlan?.links?.some(
          (link, i) =>
            i !== index && link.title === value.title && link.url === value.url
        )
      ) {
        setStatus('error');
        return false;
      }

      setStatus('');
      return true;
    },
    [wipPlan]
  );

  const handleAddNewPlanLink = useCallback(() => {
    if (!validatePlanLink(wipNewPlanLink, setWipNewPlanLinkStatus)) return;

    planEditionFormContext.onLinksChange(
      (wipPlan?.links || []).concat([wipNewPlanLink])
    );
    setWipNewPlanLink({
      title: '',
      url: '',
    });
  }, [wipPlan, wipNewPlanLink, planEditionFormContext, validatePlanLink]);

  const handleEditPlanLink = useCallback(
    (index: number, value: IPlanLink) => {
      if (!validatePlanLink(value, setEditingPlanLinkStatus, index)) return;

      planEditionFormContext.onLinksChange(
        (wipPlan?.links || []).map((link, i) => (i === index ? value : link))
      );
      setEditingPlanLinkIndex(null);
      setEditingPlanLinkValue(null);
    },
    [wipPlan, planEditionFormContext, validatePlanLink]
  );

  const toggleEditPlanLink = useCallback(
    (index: number, value: IPlanLink) => {
      if (editingPlanLinkIndex === index) {
        setEditingPlanLinkIndex(null);
        setEditingPlanLinkValue(null);
      } else {
        setEditingPlanLinkIndex(index);
        setEditingPlanLinkValue(value);
      }
    },
    [editingPlanLinkIndex]
  );

  const handleDeletePlanLink = useCallback(
    (record: { key: number | string, title: string, url: string }, index: number) => {
      planEditionFormContext.onLinksChange(
        (wipPlan?.links || []).filter((link) => link.title !== record.title && link.url !== record.url)
      )
      if (editingPlanLinkIndex === index) {
        setEditingPlanLinkIndex(null);
        setEditingPlanLinkValue(null);
        setEditingPlanLinkStatus('');
      }
    },
    [wipPlan, planEditionFormContext]
  );

  return (
    <Table
      pagination={false}
      showHeader={false}
      dataSource={[
        ...(wipPlan.links || []).sort().map((link, index) => ({
          key: index,
          title: link.title,
          url: link.url,
        })),
        {
          key: 'new',
          title: 'Nouvelle lien',
          url: '',
        },
      ]}
      tableLayout="fixed"
      columns={[
        {
          title: 'Titre',
          dataIndex: 'title',
          key: 'title',
          render: (_, record, index) => {
            return (
              <Fragment key={index}>
                {record.key === 'new' ? (
                  <Input
                    value={wipNewPlanLink.title}
                    onChange={(e) =>
                      setWipNewPlanLink({
                        ...wipNewPlanLink,
                        title: e.target.value,
                      })
                    }
                    onPressEnter={handleAddNewPlanLink}
                    placeholder="Ex: Titre du lien"
                    status={wipNewPlanLinkStatus}
                  />
                ) : editingPlanLinkIndex === index && editingPlanLinkValue ? (
                  <Input
                    value={editingPlanLinkValue?.title || ''}
                    onChange={(e) =>
                      setEditingPlanLinkValue({
                        ...editingPlanLinkValue,
                        title: e.target.value,
                      })
                    }
                    onPressEnter={() => {
                      handleEditPlanLink(index, editingPlanLinkValue);
                    }}
                    placeholder="Ex: Titre du lien"
                    status={editingPlanLinkStatus}
                  />
                ) : (
                  <span>{record.title}</span>
                )}
              </Fragment>
            );
          },
        },
        {
          title: 'URL',
          dataIndex: 'url',
          key: 'url',
          render: (_, record, index) => {
            return (
              <Fragment key={index}>
                {record.key === 'new' ? (
                  <Input
                    type="link"
                    value={wipNewPlanLink.url}
                    onChange={(e) =>
                      setWipNewPlanLink({
                        ...wipNewPlanLink,
                        url: e.target.value,
                      })
                    }
                    onPressEnter={handleAddNewPlanLink}
                    placeholder="Ex: URL du lien"
                    status={wipNewPlanLinkStatus}
                  />
                ) : editingPlanLinkIndex === index && editingPlanLinkValue ? (
                  <Input
                    type="link"
                    value={editingPlanLinkValue?.url || ''}
                    onChange={(e) =>
                      setEditingPlanLinkValue({
                        ...editingPlanLinkValue,
                        url: e.target.value,
                      })
                    }
                    onPressEnter={() => {
                      handleEditPlanLink(index, editingPlanLinkValue);
                    }}
                    placeholder="Ex: https://www.leedpulse.com/cgv"
                    status={editingPlanLinkStatus}
                  />
                ) : (
                  <span>{record.url}</span>
                )}
              </Fragment>
            );
          },
        },
        {
          title: 'Action',
          dataIndex: 'action',
          key: 'action',
          render: (_, record, index) => {
            return (
              <Flex
                key={index}
                justify="end"
                align="center"
                gap="16px"
                style={{ width: '100%' }}
              >
                {record.key === 'new' ? (
                  <Tooltip title="Ajouter" zIndex={1}>
                    <Button
                      icon={<PlusOutlined />}
                      onClick={handleAddNewPlanLink}
                    />
                  </Tooltip>
                ) : (
                  <>
                    <Tooltip title="Modifier" zIndex={1}>
                      <Button
                        icon={<EditOutlined />}
                        onClick={() => toggleEditPlanLink(index, record)}
                      />
                    </Tooltip>

                    <Tooltip title="Supprimer" zIndex={1}>
                      <Button
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDeletePlanLink(record, index)}
                      />
                    </Tooltip>
                  </>
                )}
              </Flex>
            );
          },
        },
      ]}
      style={{
        border: '1px solid var(--border-color)',
        borderRadius: '6px',
      }}
    />
  );
};

export default PlanLinksList;
